import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';

const translate = (key) => t(`quote_builder_widget.${key}`);

// The one printing block (klişe) the widget prices hot stamping with, chosen from the owner's printing blocks -- shown
// and chosen as the machines tabs add a machine (WidgetMachinesSection): the chosen one, and a popup to choose another.
const HotStampingPrintingBlockSection = ({ onChange }) => {
  const [printingBlocks, setPrintingBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [choosingId, setChoosingId] = useState(null);
  const [showChoosePopup, setShowChoosePopup] = useState(false);
  const choosePopupRef = useRef();

  const fetchPrintingBlocks = () => {
    GetRequest({
      url: '/quote-builder/widgets/hot-stamping',
      callback: (data) => {
        setPrintingBlocks(data.printing_blocks);
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        setErrorMessage(translate('failed_to_load_hot_stamping'));
      }
    });
  };

  useEffect(fetchPrintingBlocks, []);

  const closeChoosePopup = () => {
    setShowChoosePopup(false);
    if (choosePopupRef.current) {
      choosePopupRef.current.style.display = 'none';
    }
  };

  const choose = (printingBlock) => {
    setChoosingId(printingBlock.id);
    setErrorMessage('');
    PostRequest({
      url: '/quote-builder/widgets/hot-stamping-printing-block',
      data: { user_unit_price_id: printingBlock.id },
      callback: () => {
        setChoosingId(null);
        closeChoosePopup();
        fetchPrintingBlocks();
        if (onChange) { onChange(); }
      },
      404: () => {
        setChoosingId(null);
        setErrorMessage(translate('failed_to_update_hot_stamping_printing_block'));
      },
      500: () => {
        setChoosingId(null);
        setErrorMessage(translate('server_error'));
      }
    }).catch(() => {
      setChoosingId(null);
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  const printingBlocksTable = (title, rows) => (
    <table className="table table-striped table-bordered-outside" style={{ marginTop: '10px' }}>
      <tbody>
        <tr>
          <th colSpan="2"><div className="float-left">{title}</div></th>
        </tr>
        {rows}
      </tbody>
    </table>
  );

  const printingBlockRow = (printingBlock, action) => (
    <tr key={printingBlock.id} data-testid="widget-hot-stamping-printing-block">
      <td>{printingBlock.description}</td>
      <td><div className="float-right" style={{ whiteSpace: 'nowrap' }}>{action}</div></td>
    </tr>
  );

  const chosenPrintingBlock = printingBlocks.find((printingBlock) => printingBlock.widget_enabled);
  const otherPrintingBlocks = printingBlocks.filter((printingBlock) => !printingBlock.widget_enabled);

  const renderChoosePopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <h4>{translate('select_hot_stamping_printing_block')}</h4>
      {renderErrorMessage()}
      {otherPrintingBlocks.length === 0 ? (
        <p>{translate(printingBlocks.length === 0 ? 'no_hot_stamping_printing_blocks' : 'no_other_hot_stamping_printing_blocks')}</p>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {printingBlocksTable(
            t('quote_builder.printing_block_types'),
            otherPrintingBlocks.map((printingBlock) => printingBlockRow(printingBlock, (
              <a role="button" style={{ cursor: 'pointer' }} onClick={() => choose(printingBlock)}>
                {choosingId === printingBlock.id ? translate('saving') : translate('hot_stamping_printing_block_use')}
              </a>
            )))
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div style={{ padding: '10px', color: '#666' }}>{translate('loading')}</div>;
  }

  return (
    <div>
      <p style={{ color: '#666' }}>{translate('hot_stamping_printing_block_information')}</p>
      {!showChoosePopup && renderErrorMessage()}
      {chosenPrintingBlock ? (
        printingBlocksTable(translate('hot_stamping_menu_printing_block'), [printingBlockRow(chosenPrintingBlock, null)])
      ) : (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate('no_hot_stamping_printing_block_chosen')}
        </div>
      )}
      <button type="button" className="btn simple-btn" onClick={() => setShowChoosePopup(true)}>
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {translate(chosenPrintingBlock ? 'change_hot_stamping_printing_block' : 'choose_hot_stamping_printing_block')}
      </button>
      {showChoosePopup && (
        <ContentPopup
          myRef={choosePopupRef}
          autoOpen={true}
          content={renderChoosePopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowChoosePopup(false);
            setErrorMessage('');
          }}
        />
      )}
    </div>
  );
};

export default HotStampingPrintingBlockSection;
